import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  LinearProgress,
  Popover,
  Tooltip,
} from "@material-ui/core";
import { useStyles } from "./styles";
import { imageGeneration } from "Utils/emojiMapping";
import { CloseRounded, DoneRounded } from "@material-ui/icons";
import UserListPopOver from "Components/CheckinSummaryStats/UserListPopOver";

const CustomHorizontalBarChart = ({ data, checkInType = "SURVEY" }) => {
  const classes = useStyles();
  const containerRef = useRef();
  const [anchorEl, setAnchorEl] = useState(null);
  const [userList, setUserList] = useState([]);
  const [maxValue, setMaxValue] = useState([]);
  const [qusLabel, setQusLabel] = useState("lessthan12");
  const getMaxCharacterLength = (arr) => {
    if (!Array.isArray(arr) || arr.length === 0) {
      return 0;
    }
    return arr.reduce((maxLen, currentStr) => {
      const currentLength = currentStr?.text.length;
      return currentLength > maxLen ? currentLength : maxLen;
    }, 0);
  };

  useEffect(() => {
    const maxLength = getMaxCharacterLength(data?.options);
    if (data?.userResponsePieChart?.length > 0) {
      handlechartValue(data?.userResponsePieChart);
    } else if (data?.options?.length > 0) {
      handlechartValue(data?.options);
    }
    if (maxLength > 12 && maxLength < 16) {
      setQusLabel("lessthan16");
    } else if (maxLength > 16 && maxLength < 25) {
      setQusLabel("lessthan24");
    } else if (maxLength > 25) {
      setQusLabel("greaterthan25");
    }
  }, [data]);

  const handlechartValue = (chartData) => {
    const userRes = chartData?.map((res) => res.count);
    const MAX = Math.max(...userRes);
    const value = MAX + (10 - (MAX % 10));
    setMaxValue(value);
  };

  const handleClick = (event, data) => {
    event.preventDefault();
    if (data?.users?.length > 0) {
      setUserList(data?.users);
      setAnchorEl(event?.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  const handleStarRanking = (textValue) => {
    switch (textValue) {
      case "5":
        return "Five star";
      case "4":
        return "Four star";
      case "3":
        return "Three star";
      case "2":
        return "Two star";
      case "1":
        return "One star";
      default:
        return "";
    }
  };

  const getText = (text, index) => {
    switch (data?.questionType) {
      case "STARRANKING":
        return handleStarRanking(text);
      case "EMOJIRANKING":
        return (
          <img
            src={imageGeneration(text)}
            alt={text}
            className={classes.emojiImage}
          />
        );
      default:
        return (
          (checkInType === "SKILLRATING"
            ? ""
            : `${String.fromCodePoint(65 + index)}.`) + ` ${text}`
        );
    }
  };

  return (
    <Box className={classes.root}>
      <Box
        mt={1}
        className={
          qusLabel === "lessthan12"
            ? classes.labelXs
            : qusLabel === "lessthan16"
            ? classes.labelSm
            : qusLabel === "lessthan24"
            ? classes.labelMd
            : classes.labelLg
        }
      >
        <ul className={`${data?.questionType?.toLowerCase()} main-list`}>
          {data?.options?.map((res, i) => (
            <li className={`${classes.chartList} chart-list`}>
              <Tooltip title={getText(res?.text, i)}>
                <Typography
                  variant="body2"
                  className={`${classes.yAxisLabel} qus-label multi-wrap-2 break-word`}
                >
                  {getText(res?.text, i)}
                </Typography>
              </Tooltip>
              <Box
                className="cursor-pointer"
                display="flex"
                alignItems="center"
                data-testid="handleClickId"
                inputProps={{ "data-testid": "handleClick" }}
                aria-describedby={id}
                onClick={(e) => handleClick(e, res)}
              >
                <LinearProgress
                  variant="determinate"
                  value={res?.percentage}
                  className={`${classes.progressBar}`}
                />
                <Box display={"flex"} className={classes.count}>
                  <Box
                    display={"flex"}
                    className={
                      data?.questionType !== "MULTIPLECHOICE"
                        ? classes.countWrapper
                        : ""
                    }
                  >
                    <span className="font-bold font-12 mr5 count">
                      {res?.count}
                    </span>
                    {data?.questionType !== "MULTIPLECHOICE" && (
                      <span className="font-12">
                        ({res?.percentage?.toFixed(0)}
                        %)
                      </span>
                    )}
                  </Box>
                  {data?.answer?.length ? (
                    res?.correct ? (
                      <span className={classes.graphIcon}>
                        <Tooltip title="Correct answer">
                          <DoneRounded className={classes.tick} />
                        </Tooltip>
                      </span>
                    ) : (
                      <span className={classes.graphIcon}>
                        <Tooltip title="Incorrect answer">
                          <CloseRounded className={classes.cross} />
                        </Tooltip>
                      </span>
                    )
                  ) : null}
                </Box>
              </Box>
            </li>
          ))}
        </ul>
        <Box
          className={`${classes.xAxis} x-axis`}
          display="flex"
          justifyContent="space-between"
        >
          {data?.totalUsers > 1 ? (
            <>
              <span>0</span>
              {data?.totalUsers > 3 && data?.totalUsers <= 50 && (
                <span>{Math.round(data?.totalUsers / 2)}</span>
              )}
              {data?.totalUsers > 50 && (
                <>
                  <span>{Math.round(data?.totalUsers / 4)}</span>
                  <span>{Math.round(data?.totalUsers / 2)}</span>
                  <span>{Math.round((3 * data?.totalUsers) / 4)}</span>
                </>
              )}
              <span>{data?.totalUsers}</span>
            </>
          ) : (
            <>
              <span>0</span>
              <span>{data?.totalUsers}</span>
            </>
          )}
        </Box>
        <Typography
          variant="body2"
          className="graph-note f-italic font-12 f-light-gray"
        >
          Click bar graph to see the result
        </Typography>
      </Box>
      <Popover
        data-testid="handleClose"
        container={containerRef.current}
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <UserListPopOver userList={userList || []} />
      </Popover>
    </Box>
  );
};

export default CustomHorizontalBarChart;
